<template>
  <v-dialog
    :value="!!active"
    width="560"
    scrollable
    content-class="tw-self-end tw-rounded-3xl sm:tw-self-auto"
    @input="(val) => !val && close()"
  >
    <v-card v-if="active" class="tw-rounded-3xl">
      <v-card-title class="tw-flex tw-flex-col tw-items-start tw-pt-6">
        <span class="tw-text-xs tw-font-semibold tw-uppercase tw-text-primary-500">
          {{ $t('bz2.event.surveys.prompt_kicker') }}
        </span>
        <span class="tw-break-normal tw-text-xl tw-font-semibold">
          {{ active.title }}
        </span>
        <span v-if="active.speaker" class="tw-text-sm tw-text-gray-500">
          {{ active.speaker }}
        </span>
        <p
          v-if="active.description"
          class="tw-mb-0 tw-mt-2 tw-text-sm tw-font-normal tw-text-gray-600"
        >
          {{ active.description }}
        </p>
      </v-card-title>
      <v-divider />
      <v-alert
        v-if="failure"
        type="warning"
        text
        dense
        class="tw-mx-6 tw-mb-0 tw-mt-4 tw-rounded-xl"
      >
        {{ $t(`bz2.event.surveys.failure.${failure}`) }}
      </v-alert>
      <v-card-text class="tw-space-y-6 tw-py-6">
        <div v-for="question in active.questions" :key="question.key">
          <SurveyQuestionField v-model="answers[question.key]" :question="question" />
          <p
            v-if="errors[`answers.${question.key}`]"
            class="tw-mb-0 tw-mt-1 tw-text-sm tw-text-red-500"
          >
            {{ errors[`answers.${question.key}`][0] }}
          </p>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="tw-justify-end tw-space-x-2 tw-px-6 tw-py-4">
        <AppBtn color="transparent" :disabled="loading" @click="close">
          {{ $t('bz2.event.surveys.later') }}
        </AppBtn>
        <AppBtn
          color="primary"
          :disabled="!canSubmit || loading || failure === 'closed'"
          @click="submit"
        >
          <v-progress-circular
            v-if="loading"
            indeterminate
            size="16"
            width="2"
            class="tw-mr-2"
          />
          {{
            loading
              ? $t('bz2.event.surveys.sending')
              : $t('bz2.event.surveys.submit')
          }}
        </AppBtn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import AppBtn from '@/components/App/AppBtn.vue'
import SurveyQuestionField from '@/components/Surveys/SurveyQuestionField.vue'
import { onReconnected } from '@/realtime/echo'
import { SurveySubmitError } from '@/store/modules/surveys'

// El formulario de la encuesta. Se monta una vez en EventContainer: carga la
// lista de encuestas del evento y la vuelve a pedir si la conexión en tiempo
// real se cortó (los avisos de ese rato se perdieron). Lo abren el ícono de
// la barra (SurveysEvent) o la activación de la encuesta de la sala.
// Cerrarlo no pierde la encuesta: sigue disponible en el ícono.
export default {
  components: {
    AppBtn,
    SurveyQuestionField,
  },
  data: () => ({
    answers: {},
    errors: {},
    loading: false,
    // null | 'closed' | 'unavailable': el aviso amable dentro del modal.
    failure: null,
  }),
  computed: {
    ...mapState({
      active: (state) => state.surveys.active,
      drafts: (state) => state.surveys.drafts,
      enabled: (state) =>
        !!(state.event.constants.registry && state.event.constants.registry.has_surveys),
    }),
    canSubmit() {
      return this.active.questions.every(
        (question) =>
          !question.is_required ||
          (this.answers[question.key] !== null &&
            this.answers[question.key] !== undefined &&
            this.answers[question.key] !== ''),
      )
    },
  },
  watch: {
    // Encuestas apagadas en el evento (has_surveys): no se piden y el ícono
    // no aparece. El evento llega con /event/constants, después de montarse.
    enabled: {
      immediate: true,
      handler(enabled) {
        if (enabled) {
          this.$store.dispatch('surveys/load')
        } else {
          this.$store.dispatch('surveys/reset')
        }
      },
    },
    active(survey) {
      this.errors = {}
      this.failure = null
      // Si la cerró a medio llenar, vuelve a encontrar lo que había marcado.
      const draft = (survey && this.drafts[survey.uuid]) || {}
      this.answers = survey
        ? Object.fromEntries(
            survey.questions.map((question) => [
              question.key,
              draft[question.key] !== undefined ? draft[question.key] : null,
            ]),
          )
        : {}
    },
  },
  created() {
    try {
      this.stopReconnected = onReconnected(() => {
        if (this.enabled) {
          this.$store.dispatch('surveys/loadLater')
        }
      })
    } catch (e) {
      // Sin tiempo real la lista igual se carga al entrar.
    }
  },
  beforeDestroy() {
    if (this.stopReconnected) {
      this.stopReconnected()
    }
    this.$store.dispatch('surveys/reset')
  },
  methods: {
    close() {
      if (this.loading) {
        return
      }
      if (this.active && this.failure !== 'closed') {
        this.$store.dispatch('surveys/saveDraft', {
          uuid: this.active.uuid,
          answers: { ...this.answers },
        })
      }
      this.$store.dispatch('surveys/close')
    },
    async submit() {
      this.loading = true
      this.errors = {}
      this.failure = null
      try {
        await this.$store.dispatch('surveys/respond', {
          uuid: this.active.uuid,
          answers: this.answers,
        })
        this.$store.dispatch('notifications/addNotification', {
          title: this.$t('bz2.event.surveys.thanks_title'),
          body: this.$t('bz2.event.surveys.thanks_body'),
        })
      } catch (err) {
        // Nunca el error crudo: o los campos a corregir, o un aviso amable
        // con las respuestas intactas para volver a intentar.
        if (err instanceof SurveySubmitError && err.reason === 'invalid') {
          this.errors = err.fields || {}
        } else if (err instanceof SurveySubmitError && err.reason === 'closed') {
          this.failure = 'closed'
        } else {
          this.failure = 'unavailable'
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
